import dayjs from 'dayjs'
import {
  Box,
  Button,
  Card,
  Chip,
  LinearProgress,
  Typography,
} from '@mui/material'
import { Cancel, ArrowBack } from '@mui/icons-material'
import { UserIcon, CourseIcon, ConfirmIcon } from 'src/v3/core/icons'
import type { LightweightUser } from 'src/v3/manage/manage.slice'
import type { Course } from 'src/v3/core/types'
import { useLinkedServiceName } from '../../hooks/use-linked-service-name'
import type { ScheduledAssignmentFormData } from '../types'

type ScheduledAssignmentReviewStepProps = {
  formData: ScheduledAssignmentFormData
  onCancel: () => void
  onBack: () => void
  onSubmit: () => void
  onShowUsersSummary: () => void
  onShowCoursesSummary: () => void
  isSubmitting: boolean
  isEditMode?: boolean
  users?: LightweightUser[]
  courses?: Course[]
}

export default function ScheduledAssignmentReviewStep({
  formData,
  onCancel,
  onBack,
  onSubmit,
  onShowUsersSummary,
  onShowCoursesSummary,
  isSubmitting,
  isEditMode = false,
}: ScheduledAssignmentReviewStepProps) {
  let linkedServiceName = useLinkedServiceName(formData.linkedServiceID)

  let dueDaysAfterGoLive =
    formData.dueDate && formData.goLiveDate
      ? dayjs(formData.dueDate).diff(dayjs(formData.goLiveDate), 'day')
      : 0

  return (
    <>
      <Card className="p-6 mb-6">
        <Typography variant="h6" component="h2" className="mb-4">
          {isEditMode ? 'Review Changes' : 'Review Scheduled Assignment'}
        </Typography>

        <Box className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card sx={{ bgcolor: 'background.paperDark' }} className="p-6">
            <Typography variant="h6" className="mb-4">
              Scheduled Assignment Details
            </Typography>
            <Box className="flex flex-col gap-4">
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="mb-1"
                >
                  Name:
                </Typography>
                <Typography variant="body1">{formData.name}</Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="mb-1"
                >
                  Linked Service:
                </Typography>
                <Typography variant="body1">{linkedServiceName}</Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="mb-1"
                >
                  Go Live Date:
                </Typography>
                <Typography variant="body1">
                  {dayjs(formData.goLiveDate).format('MMMM D, YYYY')}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="mb-1"
                >
                  Due Date:
                </Typography>
                <Typography variant="body1">
                  {dayjs(formData.dueDate).format('MMMM D, YYYY')}
                  {dueDaysAfterGoLive > 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="span"
                      sx={{ ml: 1 }}
                    >
                      ({dueDaysAfterGoLive} days after go-live)
                    </Typography>
                  )}
                </Typography>
              </Box>
              {formData.hasRecurrence && formData.recurrence && (
                <>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      className="mb-1"
                    >
                      Recurrence:
                    </Typography>
                    <Typography variant="body1">
                      {formData.recurrence.recurrenceType === 'Monthly'
                        ? `Every ${formData.recurrence.recurrenceInterval} month${formData.recurrence.recurrenceInterval > 1 ? 's' : ''} on day ${formData.recurrence.dayOfMonth}`
                        : `Every ${formData.recurrence.recurrenceInterval} year${formData.recurrence.recurrenceInterval > 1 ? 's' : ''}`}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      className="mb-1"
                    >
                      Recurrence End Date:
                    </Typography>
                    <Typography variant="body1">
                      {formData.recurrence.recurrenceEndDate
                        ? dayjs(formData.recurrence.recurrenceEndDate).format(
                            'MMMM D, YYYY',
                          )
                        : 'No end date (repeats indefinitely)'}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Card>

          <Card sx={{ bgcolor: 'background.paperDark' }} className="p-6">
            <Typography variant="h6" className="mb-4">
              Summary
            </Typography>
            <Box className="flex flex-col gap-4">
              <Box className="flex items-center gap-2">
                <UserIcon sx={{ color: 'primary.main' }} />
                <Chip
                  label={`${formData.userIDs.length} users selected`}
                  color="primary"
                  variant="outlined"
                  onClick={onShowUsersSummary}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'primary.light' },
                  }}
                />
              </Box>
              <Box className="flex items-center gap-2">
                <CourseIcon sx={{ color: 'primary.main' }} />
                <Chip
                  label={`${formData.courseIDs.length} courses selected`}
                  color="primary"
                  variant="outlined"
                  onClick={onShowCoursesSummary}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'primary.light' },
                  }}
                />
              </Box>
            </Box>
          </Card>
        </Box>
      </Card>

      {isSubmitting && (
        <Card className="p-6 mb-6">
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" className="mt-2">
            {isEditMode
              ? 'Updating scheduled assignment...'
              : 'Creating scheduled assignment...'}
          </Typography>
        </Card>
      )}

      <Box className="flex justify-end gap-3">
        <Button
          variant="outlined"
          onClick={onCancel}
          size="large"
          startIcon={<Cancel />}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={onBack}
          size="large"
          startIcon={<ArrowBack />}
          disabled={isSubmitting}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          size="large"
          startIcon={<ConfirmIcon />}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? isEditMode
              ? 'Saving...'
              : 'Creating...'
            : isEditMode
              ? 'Save Changes'
              : 'Create Scheduled Assignment'}
        </Button>
      </Box>
    </>
  )
}
